import React, { Fragment, useEffect, useState } from "react";
import { Breadcrumbs, ToolTip } from "../AbstractElements";
import { titleCase } from "../utils/case";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { getVenues } from "../api/venue.api";
import {
  generateRosterReport,
  generateReservationsReport,
  generateSlipsReport,
  generateTransactionsReport,
  generateReceiptsReport,
  generateReceiptsBatchReport,
  getReports,
  deleteReport
} from "../api/report.api";
import { Download, RefreshCcw, Trash2 } from "react-feather";
import { toast } from "react-toastify";
import { FaFileExcel, FaFilePdf, FaFileArchive, FaFileAlt } from "react-icons/fa";
import { getDaysDiff } from "../utils/datetime";

const Report = () => {
  const { t } = useTranslation();

  const [venues, setVenues] = useState([]);

  const venueMap = React.useMemo(() => {
    const map = {};
    venues.forEach(v => {
      map[v.id] = v.name;
    });
    return map;
  }, [venues]);

  // ================= GLOBAL REPORT STORE =================
  const [reports, setReports] = useState([]);
  const [groupedReports, setGroupedReports] = useState({});
  const [availableTypes, setAvailableTypes] = useState([]);
  const [expandedType, setExpandedType] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(null);

  // ================= FILTER STATES =================
  const [filters, setFilters] = useState({
    roster: {
      date: new Date(),
      venues: [],
      reportName: "",
    },
    reservations: {
      startDate: new Date(),
      endDate: new Date(),
      venue: "",
      status: "",
      reportName: "",
    },
    slips: {
      startDate: new Date(),
      endDate: new Date(),
      venue: "",
      status: "",
      reportName: "",
    },
    transactions: {
      startDate: new Date(),
      endDate: new Date(),
      channel: "",
      status: "",
      reportName: "",
    },
    receipts: {
      startDate: new Date(),
      endDate: new Date(),
      venue: "",
      receiptFrom: "",
      receiptTo: "",
      reportName: "",
    },
    receiptsBatch: {
      startDate: new Date(),
      endDate: new Date(),
      venue: "",
      receiptFrom: "",
      receiptTo: "",
      reportName: "",
    },
  });

  const updateFilter = (module, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [field]: value,
      },
    }));
  };
  
  const fetchReports = async () => {
    try {
      const res = await getReports();
      const data = res?.data || {};

      setGroupedReports(data);
      setAvailableTypes(res?.meta?.availableTypes || []);
    } catch (err) {
      console.error(err.message);
    }
  };

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues({ perPage: 100 });
        setVenues(res?.data || []);
      } catch (err) {
        console.error(err.message);
      }
    };
    fetchVenues();
  }, []);

  useEffect(() => {
    fetchReports();
  }, []);

  const typeLabels = {
    roster: t("daily roster"),
    reservations: t("reservations list"),
    slips: t("reservation slips"),
    transactions: t("transactions list"),
    receipts: t("receipts list"),
    receipts_batch: t("receipts batch export"),
  };

  const toggleVenue = (id) => {
    const current = filters.roster.venues;

    const updated = current.includes(id)
      ? current.filter((v) => v !== id)
      : [...current, id];

    updateFilter("roster", "venues", updated);
  };

  // ================= DATE VALIDATION =================
  const validateDateRange = (start, end) => {
    if (!start || !end) return true; // allow open-ended logic handled by backend

    const s = new Date(start);
    const e = new Date(end);

    return s <= e;
  };

  // ================= GENERATE REPORT =================
  const handleGenerate = async (module, type) => {
    if (module === "daily roster") {
      if (!filters.roster.venues || filters.roster.venues.length === 0) {
        alert("Please select at least one venue.");
        return;
      }
    }

    // ================= DATE VALIDATION =================
    const getDatesByModule = () => {
      switch (module) {
        case "reservation details":
          return filters.reservations;
        case "reservation slips":
          return filters.slips;
        case "transactions":
          return filters.transactions;
        case "receipts":
          return filters.receipts;
        case "receiptsBatch":
          return filters.receiptsBatch;
        default:
          return null;
      }
    };

    const dateConfig = getDatesByModule();

    if (dateConfig) {
      const { startDate, endDate } = dateConfig;

      if (!validateDateRange(startDate, endDate)) {
        toast.error("Start date must be before or equal to end date");
        return;
      }

      // =========================
      // LIMIT: RESERVATION SLIPS MAX 7 DAYS
      // =========================
      if (module === "reservation slips" && startDate && endDate) {
        const diffDays = getDaysDiff(startDate, endDate);

        if (diffDays > 7) {
          toast.error("Reservation slips can only be generated for a maximum of 7 days per request");
          return;
        }
      }
    }

    try {
      let res;

      switch (module) {
        case "daily roster":
          res = await generateRosterReport({
            date: filters.roster.date?.toISOString().split("T")[0],
            venues: filters.roster.venues,
            type, // excel | pdf
            fileName: filters.roster.reportName
          });
          break;

        case "reservation details":
          res = await generateReservationsReport({
            startDate: filters.reservations.startDate?.toISOString().split("T")[0],
            endDate: filters.reservations.endDate?.toISOString().split("T")[0],
            venue: filters.reservations.venue,
            status: filters.reservations.status,
            type,
            fileName: filters.reservations.reportName
          });
          break;

        case "reservation slips":
          res = await generateSlipsReport({
            startDate: filters.slips.startDate?.toISOString().split("T")[0],
            endDate: filters.slips.endDate?.toISOString().split("T")[0],
            venue: filters.slips.venue,
            status: filters.slips.status,
            type: "zip",
            fileName: filters.slips.reportName
          });
          break;

        case "transactions":
          res = await generateTransactionsReport({
            startDate: filters.transactions.startDate?.toISOString().split("T")[0],
            endDate: filters.transactions.endDate?.toISOString().split("T")[0],
            channel: filters.transactions.channel,
            status: filters.transactions.status,
            type,
            fileName: filters.transactions.reportName
          });
          break;

        case "receipts":
          res = await generateReceiptsReport({
            startDate: filters.receipts.startDate?.toISOString().split("T")[0],
            endDate: filters.receipts.endDate?.toISOString().split("T")[0],
            venue: filters.receipts.venue,
            receiptFrom: filters.receipts.receiptFrom?.trim(),
            receiptTo: filters.receipts.receiptTo?.trim(),
            type,
            fileName: filters.receipts.reportName
          });
          break;

        case "receiptsBatch":
          res = await generateReceiptsBatchReport({
            startDate: filters.receiptsBatch.startDate?.toISOString().split("T")[0],
            endDate: filters.receiptsBatch.endDate?.toISOString().split("T")[0],
            venue: filters.receiptsBatch.venue,
            receiptFrom: filters.receiptsBatch.receiptFrom?.trim(),
            receiptTo: filters.receiptsBatch.receiptTo?.trim(),
            type: "zip",
            fileName: filters.receiptsBatch.reportName
          });
          break;

        default:
          throw new Error("Unknown report module");
      }

      toast.success("Report queued. Come back later to view generated report");
      fetchReports();
    } catch (err) {
      console.error(err.message);
    }
  };

  // ================= DELETE =================
  const handleDownload = (filePath) => {
    if (!filePath) return;
    window.open(`https://bfiles.chinfumc.org.my/${filePath}`, "_blank");
  };

  const handleDeleteReport = async (id) => {
    try {
      await deleteReport(id);

      setGroupedReports((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((type) => {
          updated[type] = updated[type].filter((r) => r.id !== id);
        });

        return updated;
      });

      toast.success("Report deleted successfully");
    } catch (err) {
      console.error(err.message);
    }
  };

  const toggleTooltip = (id) => {
    setTooltipOpen((prev) => (prev === id ? null : id));
  };
  
  const formatConfig = (config) => {
    if (!config) return "-";

    return Object.entries(config)
      .map(([key, value]) => {
        if (value === null || value === undefined || value === "") return null;

        // ================= EXCLUDE exportType =================
        if (key === "exportType") return null;

        // ================= VENUE MAPPING =================
        if (key === "venues" && Array.isArray(value)) {
          const names = value
            .map((id) => venueMap[id] || id)
            .join(", ");

          return `${titleCase(key)}: ${names}`;
        }

        return `${titleCase(key)}: ${Array.isArray(value) ? value.join(", ") : value}`;
      })
      .filter(Boolean)
      .join("\n");
  };

  const getFileIcon = (exportType) => {
    switch (exportType) {
      case "excel":
        return <FaFileExcel color="green" size={16} />;
      case "pdf":
        return <FaFilePdf color="red" size={16} />;
      case "zip":
        return <FaFileArchive color="orange" size={16} />;
      default:
        return <FaFileAlt color="gray" size={16} />;
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reports"))}
        title={titleCase(t("reports"))}
        mainTitle={titleCase(t("reports"))}
      />

      <Row>
        {/* ================= SIDEBAR ================= */}
        <Col xl="3" className="box-col-3 pe-0 d-flex">
          <Card className="flex-fill">
            <CardBody
              style={{
                height: "100%",
                overflowY: "auto",
              }}
            >
              <h6>{titleCase(t("generated reports"))}</h6>

              {availableTypes.length === 0 && (
                <div className="text-muted mt-2" style={{ fontSize: "12px" }}>
                  {titleCase(t("no reports generated yet"))}
                </div>
              )}

              {availableTypes.map((type) => {
                const items = groupedReports?.[type] || [];

                return (
                  <div key={type} className="mb-2">

                    {/* ================= TYPE BUTTON ================= */}
                    <Button
                      color="light"
                      block
                      className="text-start d-flex justify-content-between align-items-center"
                      onClick={() =>
                        setExpandedType(expandedType === type ? null : type)
                      }
                    >
                      <span>
                        {titleCase(typeLabels[type] || type)}
                      </span>

                      <span className="badge bg-primary">
                        {items.length}
                      </span>
                    </Button>

                    {/* ================= EXPANDED LIST ================= */}
                    {expandedType === type && (
                      <div className="mt-2 ms-2">
                        {items.length === 0 && (
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            {t("no files")}
                          </div>
                        )}

                        {items.map((r) => (
                          <>
                            <div
                              id={`report-${r.id}`}
                              className="d-flex justify-content-between align-items-center p-2 border mb-1"
                              style={{ fontSize: "12px" }}
                              onMouseEnter={() => toggleTooltip(r.id)}
                              onMouseLeave={() => toggleTooltip(null)}
                            >
                              {/* ================= LEFT CONTENT ================= */}
                              <div className="d-flex align-items-center gap-3">
                                {getFileIcon(r.configuration?.exportType)}

                                <div>
                                  {r.reportName}
                                  <br />
                                  <small className="text-muted">
                                    {new Date(r.createdAt).toLocaleString("en-MY")}
                                  </small>
                                </div>
                              </div>

                              {/* ================= ACTION BUTTONS ================= */}
                              <div className="d-flex gap-2">

                                {/* DOWNLOAD */}
                                {r.documentPath && 
                                  <Download
                                    size={18}
                                    style={{ cursor: "pointer", color: "green" }}
                                    title="Download"
                                    onClick={() => handleDownload(r.documentPath)}
                                  />
                                }

                                {/* Refresh */}
                                {!r.documentPath && 
                                  <RefreshCcw
                                    size={18}
                                    style={{ cursor: "pointer", color: "orange" }}
                                    title="Download"
                                    onClick={() => fetchReports()}
                                  />
                                }

                                {/* DELETE */}
                                <Trash2
                                  size={18}
                                  style={{ cursor: "pointer", color: "red" }}
                                  title="Delete"
                                  onClick={() => handleDeleteReport(r.id)}
                                />

                              </div>
                            </div>
                            <ToolTip
                              attrToolTip={{
                                placement: "right",
                                isOpen: tooltipOpen === r.id,
                                target: `report-${r.id}`,
                                toggle: () => toggleTooltip(r.id),
                              }}
                            >
                              <div style={{ whiteSpace: "pre-line", textAlign: "left" }}>
                                {formatConfig(r.configuration)}
                              </div>
                            </ToolTip>
                          </>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardBody>
          </Card>
        </Col>

        {/* ================= MAIN CONTENT ================= */}
        <Col xl="9" md="12">
          <Row className="g-3">

            {/* ===== DAILY ROSTER ===== */}
            <Col md="4" className="d-flex">
              <Card className="flex-fill">
                <CardBody className="d-flex flex-column">
                  <h6>{titleCase(t("daily roster"))}</h6>

                  <FormGroup>
                    <Label>{titleCase(t("report name"))}</Label>
                    <Input
                      type="text"
                      placeholder="default: Daily Roster_[timestamp]"
                      value={filters.roster.reportName}
                      onChange={(e) =>
                        updateFilter("roster", "reportName", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("date"))}</Label>
                    <DatePicker
                      className="form-control"
                      selected={filters.roster.date}
                      onChange={(d) => updateFilter("roster", "date", d)}
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("venues"))}</Label>
                    <Row>
                      {venues.map((v) => (
                        <Col md="12" key={v.id}>
                          <div className="checkbox checkbox-primary">
                            <Input
                              type="checkbox"
                              id={`roster-${v.id}`}
                              checked={filters.roster.venues.includes(v.id)}
                              onChange={() => toggleVenue(v.id)}
                            />
                            <Label for={`roster-${v.id}`}>{v.name}</Label>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </FormGroup>

                  <Row className="g-2 mt-auto">
                    <Col md="6">
                      <Button color="success" block onClick={() => handleGenerate("daily roster", "excel")}>
                        {titleCase(t("excel"))}
                      </Button>
                    </Col>
                    <Col md="6">
                      <Button color="danger" block onClick={() => handleGenerate("daily roster", "pdf")}>
                        PDF
                      </Button>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
            </Col>

            {/* ===== DETAILS ===== */}
            <Col md="4" className="d-flex">
              <Card className="flex-fill">
                <CardBody className="d-flex flex-column">
                  <h6>{titleCase(t("reservations list"))}</h6>

                  <FormGroup>
                    <Label>{titleCase(t("report name"))}</Label>
                    <Input
                      type="text"
                      placeholder="default: Reservations List_[timestamp]"
                      value={filters.reservations.reportName}
                      onChange={(e) =>
                        updateFilter("reservations", "reportName", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("start date"))}</Label>
                    <DatePicker
                      selected={filters.reservations.startDate}
                      onChange={(d) => updateFilter("reservations", "startDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("end date"))}</Label>
                    <DatePicker
                      selected={filters.reservations.endDate}
                      onChange={(d) => updateFilter("reservations", "endDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />

                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("venue"))}</Label>
                    <Input
                      value={filters.reservations.venue}
                      onChange={(e) => updateFilter("reservations", "venue", e.target.value)}
                      type="select"
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("status"))}</Label>
                    <Input
                      type="select"
                      value={filters.reservations.status}
                      onChange={(e) => updateFilter("reservations", "status", e.target.value)}
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="pending">{titleCase(t("pending"))}</option>
                      <option value="confirmed">{titleCase(t("confirmed"))}</option>
                      <option value="approved">{titleCase(t("approved"))}</option>
                      <option value="cancelled">{titleCase(t("cancelled"))}</option>
                    </Input>
                  </FormGroup>

                  <Row className="g-2 mt-auto">
                    <Col md="6">
                      <Button color="success" block onClick={() => handleGenerate("reservation details", "excel")}>
                        {titleCase(t("excel"))}
                      </Button>
                    </Col>
                    <Col md="6">
                      <Button color="danger" block onClick={() => handleGenerate("reservation details", "pdf")}>
                        PDF
                      </Button>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
            </Col>

            {/* ===== SLIPS ===== */}
            <Col md="4" className="d-flex">
              <Card className="flex-fill">
                <CardBody className="d-flex flex-column">
                  <h6>{titleCase(t("reservation slips"))}</h6>

                  <FormGroup>
                    <Label>{titleCase(t("report name"))}</Label>
                    <Input
                      type="text"
                      placeholder="default: Reservation Slips_[timestamp]"
                      value={filters.slips.reportName}
                      onChange={(e) =>
                        updateFilter("slips", "reportName", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("start date"))}</Label>
                    <DatePicker
                      selected={filters.slips.startDate}
                      onChange={(d) => updateFilter("slips", "startDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("end date"))}</Label>
                    <DatePicker
                      selected={filters.slips.endDate}
                      onChange={(d) => updateFilter("slips", "endDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("venue"))}</Label>
                    <Input
                      type="select"
                      value={filters.slips.venue}
                      onChange={(e) => updateFilter("slips", "venue", e.target.value)}
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("status"))}</Label>
                    <Input
                      value={filters.slips.status}
                      onChange={(e) => updateFilter("slips", "status", e.target.value)}
                      type="select"
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="pending">{titleCase(t("pending"))}</option>
                      <option value="confirmed">{titleCase(t("confirmed"))}</option>
                      <option value="approved">{titleCase(t("approved"))}</option>
                      <option value="cancelled">{titleCase(t("cancelled"))}</option>
                    </Input>
                  </FormGroup>

                  <Row className="g-2 mt-auto">
                    <Col md="12">
                      <Button
                        color="info"
                        block
                        onClick={() => handleGenerate("reservation slips", "zip")}
                      >
                        {titleCase(t("download"))}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* ===== TRANSACTIONS ===== */}
            <Col md="4" className="d-flex">
              <Card className="flex-fill">
                <CardBody className="d-flex flex-column">

                  <h6>{titleCase(t("transactions list"))}</h6>

                  <FormGroup>
                    <Label>{titleCase(t("report name"))}</Label>
                    <Input
                      type="text"
                      placeholder="default: Transactions List_[timestamp]"
                      value={filters.transactions.reportName}
                      onChange={(e) =>
                        updateFilter("transactions", "reportName", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("start date"))}</Label>
                    <DatePicker
                      selected={filters.transactions.startDate}
                      onChange={(d) => updateFilter("transactions", "startDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("end date"))}</Label>
                    <DatePicker
                      selected={filters.transactions.endDate}
                      onChange={(d) => updateFilter("transactions", "endDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("payment channel"))}</Label>
                    <Input
                      value={filters.transactions.channel}
                      onChange={(e) => updateFilter("transactions", "channel", e.target.value)}
                      type="select"
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="bank_transfer">{titleCase(t("bank transfer"))}</option>
                      <option value="fpx">{t("FPX")}</option>
                      <option value="s_pay_global">{titleCase(t("s pay global"))}</option>
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("status"))}</Label>
                    <Input
                      value={filters.transactions.status}
                      onChange={(e) => updateFilter("transactions", "status", e.target.value)}
                      type="select"
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="paid">{titleCase(t("paid"))}</option>
                      <option value="pending">{titleCase(t("pending"))}</option>
                      <option value="failed">{titleCase(t("failed"))}</option>
                    </Input>
                  </FormGroup>

                  {/* ===== ACTION BUTTONS (STICK BOTTOM) ===== */}
                  <Row className="g-2 mt-auto">
                    <Col md="6">
                      <Button
                        color="success"
                        block
                        onClick={() =>
                          handleGenerate("transactions", "excel")
                        }
                      >
                        {titleCase(t("excel"))}
                      </Button>
                    </Col>

                    <Col md="6">
                      <Button
                        color="danger"
                        block
                        onClick={() =>
                          handleGenerate("transactions", "pdf")
                        }
                      >
                        PDF
                      </Button>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
            </Col>

            {/* ===== RECEIPTS ===== */}
            <Col md="4" className="d-flex">
              <Card className="flex-fill">
                <CardBody className="d-flex flex-column">

                  <h6>{titleCase(t("receipts list"))}</h6>

                  <FormGroup>
                    <Label>{titleCase(t("report name"))}</Label>
                    <Input
                      type="text"
                      placeholder="default: Receipts List_[timestamp]"
                      value={filters.receipts.reportName}
                      onChange={(e) =>
                        updateFilter("receipts", "reportName", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("start date"))}</Label>
                    <DatePicker
                      selected={filters.receipts.startDate}
                      onChange={(d) => updateFilter("receipts", "startDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("end date"))}</Label>
                    <DatePicker
                      selected={filters.receipts.endDate}
                      onChange={(d) => updateFilter("receipts", "endDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("venue"))}</Label>
                    <Input
                      type="select"
                      value={filters.receipts.venue}
                      onChange={(e) => updateFilter("receipts", "venue", e.target.value)}
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      {venues.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("receipt number from"))}</Label>
                    <Input
                      type="text"
                      value={filters.receipts.receiptFrom}
                      onChange={(e) => updateFilter("receipts", "receiptFrom", e.target.value)}
                      placeholder="e.g. 1001"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("receipt number to"))}</Label>
                    <Input
                      type="text"
                      value={filters.receipts.receiptTo}
                      onChange={(e) => updateFilter("receipts", "receiptTo", e.target.value)}
                      placeholder="e.g. 2000"
                    />
                  </FormGroup>

                  {/* ===== ACTION BUTTONS ===== */}
                  <Row className="g-2 mt-auto">
                    <Col md="6">
                      <Button
                        color="success"
                        block
                        onClick={() => handleGenerate("receipts", "excel")}
                      >
                        {titleCase(t("excel"))}
                      </Button>
                    </Col>

                    <Col md="6">
                      <Button
                        color="danger"
                        block
                        onClick={() => handleGenerate("receipts", "pdf")}
                      >
                        PDF
                      </Button>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
            </Col>

            {/* ===== RECEIPTS BATCH EXPORT ===== */}
            <Col md="4" className="d-flex">
              <Card className="flex-fill">
                <CardBody className="d-flex flex-column">

                  <h6>{titleCase(t("receipts batch export"))}</h6>

                  <FormGroup>
                    <Label>{titleCase(t("report name"))}</Label>
                    <Input
                      type="text"
                      placeholder="default: Receipts Batch Export_[timestamp]"
                      value={filters.receiptsBatch.reportName}
                      onChange={(e) =>
                        updateFilter("receiptsBatch", "reportName", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("start date"))}</Label>
                    <DatePicker
                      selected={filters.receiptsBatch.startDate}
                      onChange={(d) => updateFilter("receiptsBatch", "startDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("end date"))}</Label>
                    <DatePicker
                      selected={filters.receiptsBatch.endDate}
                      onChange={(d) => updateFilter("receiptsBatch", "endDate", d)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("venue"))}</Label>
                    <Input
                      type="select"
                      value={filters.receiptsBatch.venue}
                      onChange={(e) => updateFilter("receiptsBatch", "venue", e.target.value)}
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("receipt number from"))}</Label>
                    <Input
                      type="text"
                      value={filters.receiptsBatch.receiptFrom}
                      onChange={(e) => updateFilter("receiptsBatch", "receiptFrom", e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("receipt number to"))}</Label>
                    <Input
                      type="text"
                      value={filters.receiptsBatch.receiptTo}
                      onChange={(e) => updateFilter("receiptsBatch", "receiptTo", e.target.value)}
                    />
                  </FormGroup>

                  <Row className="g-2 mt-auto">
                    <Col md="12">
                      <Button
                        color="info"
                        block
                        onClick={() => handleGenerate("receiptsBatch", "zip")}
                      >
                        {titleCase(t("download"))}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Fragment>
  );
};

export default Report;